import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Bell, X, AlertTriangle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const POLL_MS = 60 * 1000; // every minute
const SNOOZE_MS = 30 * 60 * 1000; // 30 min

/**
 * Global Task Reminder Popup.
 * Polls /tasks/my/pending-reminders every minute and surfaces a non-blocking
 * toast-style stack in the bottom-right whenever the logged-in user has
 * active tasks. Each card can be snoozed for 30 minutes (per session).
 */
export default function TaskReminderPopup() {
  const { API, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reminders, setReminders] = useState([]);
  const [snoozed, setSnoozed] = useState({}); // { task_id: timestampToShowAgain }

  const fetchReminders = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/tasks/my/pending-reminders`);
      setReminders(res.data || []);
    } catch (e) {
      // silently ignore — could be network blip
    }
  }, [API, token]);

  useEffect(() => {
    if (!user || !token) return;
    fetchReminders();
    const t = setInterval(fetchReminders, POLL_MS);
    return () => clearInterval(t);
  }, [user, token, fetchReminders]);

  // Hide on login / portal pages
  if (!user || !token) return null;
  if (location.pathname.startsWith('/portal') || location.pathname.startsWith('/partner') || location.pathname.startsWith('/login') || location.pathname.startsWith('/reports')) return null;

  const now = Date.now();
  const visible = reminders.filter((r) => !snoozed[r.task_id] || snoozed[r.task_id] < now);

  if (visible.length === 0) return null;

  // Show only top 3 to avoid clutter
  const top = visible.slice(0, 3);
  const rest = visible.length - top.length;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 w-80 max-w-[calc(100vw-2rem)]" data-testid="task-reminder-popup">
      {top.map((t) => {
        const overdue = t.overdue;
        return (
          <div
            key={t.task_id}
            className={`rounded-lg shadow-lg border p-3 bg-white animate-in slide-in-from-right-5 ${overdue ? 'border-red-300' : 'border-amber-300'}`}
            data-testid={`reminder-${t.task_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                {overdue ? (
                  <span className="flex items-center gap-1 text-red-600"><AlertTriangle className="h-4 w-4" /> Overdue</span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-600"><Bell className="h-4 w-4" /> Task Reminder</span>
                )}
              </div>
              <button
                onClick={() => setSnoozed((s) => ({ ...s, [t.task_id]: Date.now() + SNOOZE_MS }))}
                className="text-slate-400 hover:text-slate-700"
                title="Snooze 30 min"
                data-testid={`reminder-snooze-${t.task_id}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="font-medium text-slate-900 mt-1 line-clamp-2">{t.title}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
              <Clock className="h-3 w-3" />
              {overdue
                ? `Was due ${formatDistanceToNow(new Date(t.deadline))} ago`
                : `Due in ${formatDistanceToNow(new Date(t.deadline))}`}
              {t.assigned_by_name && <span> · by {t.assigned_by_name}</span>}
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                className="flex-1 h-8 bg-slate-900 hover:bg-slate-800 text-xs"
                onClick={() => navigate('/tasks')}
                data-testid={`reminder-open-${t.task_id}`}
              >
                Open Task Manager
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setSnoozed((s) => ({ ...s, [t.task_id]: Date.now() + SNOOZE_MS }))}
              >
                Snooze 30m
              </Button>
            </div>
          </div>
        );
      })}
      {rest > 0 && (
        <div className="text-center text-xs text-slate-600 bg-white/90 rounded border border-slate-200 py-1">
          + {rest} more open task{rest > 1 ? 's' : ''} —{' '}
          <button className="text-indigo-600 hover:underline" onClick={() => navigate('/tasks')}>view all</button>
        </div>
      )}
    </div>
  );
}
